
import { IntlShape } from "react-intl"
import zlib from 'pako';
import { GetToken } from "@zerolight-core/zl-auth-handler"
import { IBuildSession } from "@zerolight-core/generic-buildsession"
import { BuildData, JSONValue } from "@zerolight-core/iframe-communications"
import { AnimationStates, PartialFrameConfig, ZLBoltDirect2D } from "@zerolight-core/libzl"
import { DefaultService, getInitialUrlArgs } from "./UrlArgsProvider"
import { CustomWrap } from "./types/VisualizerTypes"
import { MasterModel } from "./BuildSession"
import { requireToken } from "./utils/auth";

export interface OpaqueBuildData {
	creator?: string
	session: string
	customWraps: Record<string, CustomWrap>
	appliedWraps?: Record<string, string>
	masterModel?: MasterModel
	prcodes?: string[]
}

export const getBuildData = async (intl: IntlShape, masterModel: MasterModel, prcodes: string[], animations: AnimationStates | undefined, environment: string, build: IBuildSession, customWraps: Record<string, CustomWrap>, appliedWraps: Record<string, string>, d2d?: ZLBoltDirect2D, userTriggered?: boolean, noWait?: boolean) : Promise<BuildData> => {
	const {parentPath, assetService, shortcodeService, showPrices, forceRenderChain} = getInitialUrlArgs()

	const state : OpaqueBuildData = {
		session: await build?.getSaveData().text(),
		customWraps,
		appliedWraps,
		masterModel,
		prcodes
	}

	const data : BuildData = {
		modified: (new Date()).toISOString(),
		modelName: build.getModelName(),
		modelYear: build.getModelYear(),
		imageUrl: "",
		transparentImageUrl: "",
		//price: build.getTotalPrice(),
		//shortcode: "",
		data: state as unknown as JSONValue
	}

	if(showPrices) {
		const price = build.getPricingInfo({apr: 0, months: 1, downpayment: 0, tradeIn: 0})
		data.price = (price?.totalValue && price?.currency) ? {amount: price.totalValue, currency: price.currency} : undefined
	}

	const car = "slate truck 2025"
	const camera = "Ext_FrontThreeQuarter_Portrait"
	const width = 1024
	const height = 1024
	const backgroundColour = "#26262600"

	const prCodePlusWaterMark = [...prcodes];			
	prCodePlusWaterMark.push('WATERMARK');
	//console.log(prCodePlusWaterMark)

	let token = undefined;
	if(requireToken(assetService)) {
		token = await GetToken([`renderingservice.omnistream.${assetService}.access`])
	}
	if(token && d2d && !forceRenderChain) {
		const config : PartialFrameConfig = {
			car,
			prcodes: prCodePlusWaterMark.join(","),
			environment,
			camera: {
				specification: "named",
				name: camera
			},
			size: [width, height],
			anim_state: animations || {},
			render_quality: "Custom",
			render_hints: {
				custom_msaa: 2,
				custom_ssaa: 2
			},
			format: "jpg"
		}
		const frameConfig = d2d.makeFrameConfig(config)
		const transparentConfig : PartialFrameConfig = {
			...config,
			prcodes: prcodes.join(","),
			anim_state: {},
			environment: "parking_lot",
			background_colour: backgroundColour,
			camera: {
				specification: "named",
				name: "Ext_FrontThreeQuarter_AlphaShare"
			},
			format: "png",
			size: [1620, 1080]
		}
		const transparentFrameConfig = d2d.makeFrameConfig(transparentConfig)

		console.log("Generating share image (makeFrameURL): "+d2d.makeFrameURL(frameConfig.getFilename(), true))
		
		const frameRequest = d2d.fetchFrames([frameConfig, transparentFrameConfig], {loadImages: false, originator: "user_direct", usage: "initial_frame"})

		if(!noWait) {
			await frameRequest.framesCompletePromise
		}
		
		data.imageUrl = frameRequest.frames[frameConfig.getFilename()].url
		data.transparentImageUrl = frameRequest.frames[transparentFrameConfig.getFilename()].url
		console.log("Generating share image (frameRequest): "+data.imageUrl)

	}
	else {
		const config : Record<string, string> = {
			car,
			prcodes: prCodePlusWaterMark.join(","),
			env: environment,
			camera,
			width: width.toString(),
			height: height.toString()
		}
		if(token) {
			config.token = token.token
		}
		const shareConfig : Record<string, string> = {
			...config,
			format: "jpg"
		}
		if(animations && Object.keys(animations).length) {
			shareConfig.anim_state = JSON.stringify(animations)
		}
		const rcParams = new URLSearchParams(shareConfig)
		data.imageUrl = `https://renderchain-us-east-1-${assetService}.omnistream.zlthunder.net/api/image/?${rcParams}`

		const transparentRcParams = new URLSearchParams({
			...config,
			prcodes: prcodes.join(","),
			environment: "parking_lot",
			format: "png",
			background_colour: backgroundColour.replace(/^#/, ""),
			width: "1620",
			height: "1080",
			camera: "Ext_FrontThreeQuarter_AlphaShare"
		})
		data.transparentImageUrl = `https://renderchain-us-east-1-${assetService}.omnistream.zlthunder.net/api/image/?${transparentRcParams}`

		if(userTriggered) {
			const promises = [fetch(data.imageUrl), fetch(data.transparentImageUrl)]
			if(!noWait) {
				await Promise.allSettled(promises)
			}
		}
	}

	if(userTriggered) {
		const title = intl.formatMessage({id: "share.default_title", defaultMessage: "Check out my {model}"}, {model: build?.getModelName()})

		const urlParams = new URLSearchParams({
			"shortcode": "${code}"
		})
		if(window.location.origin !==  "https://configurator-live.slate.zlthunder.net") {
			urlParams.set("iframeOrigin", window.location.origin)
		}
		if(assetService !== DefaultService) {
			urlParams.set("assetService", assetService)
		}
		if(shortcodeService !== assetService) {
			urlParams.set("shortcodeService", shortcodeService)
		}
		
		const compressedState : string = btoa(String.fromCharCode(...zlib.deflate(JSON.stringify(state))))

		const response = await fetch(`https://shortcode-${shortcodeService}.omnistream.zlthunder.net/code`, {
			method: "POST",
			headers: {
				'accept': 'application/json',
				'content-type': 'application/json'
			},
			body: JSON.stringify({
				blob: {
					sharing: {
						title,
						url: `${parentPath}?${urlParams}`,
						image: data.imageUrl
					},
					state: compressedState
				}
			})
		})
		const shortcodeData = await response.json()
		data.shortcode = shortcodeData.shortCode
	}

	return data
}
